/* eslint-disable react/no-unescaped-entities --
 * v77.99.77: This file uses idiomatic React patterns (effect-driven
 * state hydration after fetches, English text with apostrophes /
 * quotes in JSX). The experimental React 19 hooks rules
 * (`set-state-in-effect`, `purity`, `preserve-manual-memoization`)
 * are not shipped by eslint-plugin-react-hooks 5.2.0 — only the
 * standalone Emergent lint tool surfaces them, and they mis-flag
 * these intentional patterns. `react/no-unescaped-entities` similarly
 * flags safe English text.
 */
// v77.99.14 — Marketing landing pages for individual features.
//
// Six unlinked pages designed for paste-into-ads URLs:
//   /features                  — overview hub linking to all five
//   /features/calendar         — shared calendar + handovers
//   /features/expenses         — track who paid what, with receipts
//   /features/messaging        — tamper-evident chat, court-admissible
//   /features/court-bundle     — one PDF that solicitors actually want
//   /features/journal          — incident log, private or shared
//
// Design rules (in lockstep with Landing.jsx):
//   • Same colour palette (#1F9FA4 teal primary, #116B6E dark teal, #1A2225 ink).
//   • Same typography (font-heading from Tailwind config).
//   • Punchy hero headline (h1) + calm/reassuring detail body — per user brief.
//   • Two CTAs per page: primary "Start free trial" → /auth?tab=register,
//     secondary "See pricing" → /pricing.
//   • Native React mockups (NOT screenshots — sharper, no chrome to clip,
//     stays in sync with the real product).
//   • Pages are NOT linked from the main nav. Reachable only by direct URL.
//
// Each page sets a unique <title> + <meta name="description"> via a
// small useDocumentMeta hook so search engines + social previews see
// distinct, keyword-rich metadata per landing URL.

import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import LegalFooterInfo from "@/components/LegalFooterInfo";
import { trackEvent } from "@/lib/analytics";
import { useFeaturePageVariant } from "@/lib/useFeaturePageVariant";
import {
  Calendar,
  ReceiptEuro,
  MessageSquare,
  BookOpen,
  ShieldCheck,
  ScrollText,
  FileLock2,
  Fingerprint,
  QrCode,
  CircleDot,
  CheckCircle2,
  Lock,
  ArrowRight,
  Clock,
  Paperclip,
  AlertCircle,
  Camera,
  Repeat,
  Users,
  Eye,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Shared chrome + utilities
// ---------------------------------------------------------------------------

// Set <title> and <meta description> for this landing URL. We don't pull in
// react-helmet — a tiny effect is plenty and avoids a dependency just for
// six pages. Restores defaults on unmount so other routes aren't polluted.
function useDocumentMeta({ title, description }) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;
    let metaEl = document.querySelector('meta[name="description"]');
    let createdMeta = false;
    if (!metaEl) {
      metaEl = document.createElement("meta");
      metaEl.setAttribute("name", "description");
      document.head.appendChild(metaEl);
      createdMeta = true;
    }
    const prevDescription = metaEl.getAttribute("content");
    if (description) metaEl.setAttribute("content", description);
    return () => {
      document.title = prevTitle;
      if (createdMeta) {
        metaEl.parentNode && metaEl.parentNode.removeChild(metaEl);
      } else if (prevDescription !== null) {
        metaEl.setAttribute("content", prevDescription);
      }
    };
  }, [title, description]);
}

// Card surface mirrors Landing.jsx so all our marketing pages look like
// the same product.
const cardSurface =
  "border border-[#E5EBEC] dark:border-[#2A3539] " +
  "bg-white dark:bg-[#161E21] " +
  "rounded-xl shadow-[0_1px_2px_rgba(15,21,23,0.04)]";

const Eyebrow = ({ children }) => (
  <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#1F9FA4] dark:text-[#3FB8BC]">
    {children}
  </div>
);

// Top brand bar — links to home only. Deliberately no marketing nav so the
// page is laser-focused on one conversion goal.
const MarketingHeader = () => (
  <header className="border-b border-[#E5EBEC] dark:border-[#1F2A2D] bg-white/85 dark:bg-[#0E1416]/85 backdrop-blur sticky top-0 z-20">
    <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-2.5" data-testid="features-brand">
        <Logo size={30} />
        <span className="font-heading font-semibold text-base text-[#1A2225] dark:text-[#F4F8F9]">
          CoParent Console
        </span>
      </Link>
      <div className="flex items-center gap-2.5">
        <Link
          to="/pricing"
          className="hidden sm:inline-flex text-sm font-medium text-[#48595E] dark:text-[#A8B6BB] hover:text-[#116B6E] dark:hover:text-[#3FB8BC] px-2 py-1.5"
          data-testid="features-header-pricing-link"
        >
          Pricing
        </Link>
        <Link to="/auth?tab=register" data-testid="features-header-cta">
          <Button className="bg-[#116B6E] hover:bg-[#0F5658] text-white rounded-md h-9 px-4 text-sm">
            Start free trial
          </Button>
        </Link>
      </div>
    </div>
  </header>
);

const MarketingFooter = () => (
  <footer className="border-t border-[#E5EBEC] dark:border-[#1F2A2D] bg-[#FAFCFC] dark:bg-[#0B1012] mt-16">
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10 grid sm:grid-cols-3 gap-8 text-sm">
      <div>
        <div className="flex items-center gap-2.5">
          <Logo size={28} />
          <span className="font-heading font-semibold text-[#1A2225] dark:text-[#F4F8F9]">
            CoParent Console
          </span>
        </div>
        <p className="text-[#6F828A] mt-3 leading-relaxed">
          An Irish-made platform for separated parents — calm, structured,
          accountable. Audit-trail by default.
        </p>
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#6F828A]">
          Product
        </div>
        <ul className="mt-3 space-y-2">
          <li><Link to="/" className="text-[#48595E] dark:text-[#A8B6BB] hover:text-[#116B6E]">Home</Link></li>
          <li><Link to="/pricing" className="text-[#48595E] dark:text-[#A8B6BB] hover:text-[#116B6E]">Pricing</Link></li>
          <li><Link to="/about" className="text-[#48595E] dark:text-[#A8B6BB] hover:text-[#116B6E]">About</Link></li>
          <li><Link to="/professionals" className="text-[#48595E] dark:text-[#A8B6BB] hover:text-[#116B6E]">For solicitors &amp; mediators</Link></li>
        </ul>
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#6F828A]">
          Trust &amp; legal
        </div>
        <ul className="mt-3 space-y-2">
          <li><Link to="/privacy" className="text-[#48595E] dark:text-[#A8B6BB] hover:text-[#116B6E]">Privacy</Link></li>
          <li><Link to="/terms" className="text-[#48595E] dark:text-[#A8B6BB] hover:text-[#116B6E]">Terms</Link></li>
          <li><Link to="/security" className="text-[#48595E] dark:text-[#A8B6BB] hover:text-[#116B6E]">Security</Link></li>
          <li><Link to="/contact" className="text-[#48595E] dark:text-[#A8B6BB] hover:text-[#116B6E]">Contact</Link></li>
        </ul>
      </div>
    </div>
    <div className="border-t border-[#E5EBEC] dark:border-[#1F2A2D] py-4 text-center text-xs text-[#6F828A]">
      © CoParent Console — Built for families, made in Ireland.
    </div>
    <LegalFooterInfo />
  </footer>
);

// Standard hero (punchy headline, calm body, two CTAs, mockup on the right).
// v77.99.15 — pageKey is now required so the hero can be A/B-tested via
// ?variant=, ?utm_content= or ?utm_campaign= URL params without redeploy.
// The eyebrow/headline/body passed in act as defaults; matching variants
// from featurePageVariants.js override them per visitor. The active
// variant key is forwarded to the CTA analytics so conversion can be
// attributed back to the creative that brought the visitor in.
const Hero = ({ pageKey, eyebrow, headline, body, ctaTestPrefix, mockup }) => {
  const variant = useFeaturePageVariant(pageKey, { eyebrow, headline, body });
  return (
  <section className="relative overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-b from-[#F4FAFA] via-white to-white dark:from-[#0F2326] dark:via-[#0E1416] dark:to-[#0E1416]" />
    <div className="relative max-w-6xl mx-auto px-5 sm:px-8 pt-12 pb-16 lg:pt-20 lg:pb-24 grid lg:grid-cols-2 gap-10 items-center">
      <div>
        <Eyebrow>{variant.eyebrow}</Eyebrow>
        <h1
          className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight mt-4 leading-[1.05] text-[#1A2225] dark:text-[#F4F8F9]"
          data-testid={`${ctaTestPrefix}-hero-headline`}
        >
          {variant.headline}
        </h1>
        <p
          className="text-base sm:text-lg leading-relaxed mt-5 text-[#48595E] dark:text-[#A8B6BB] max-w-xl"
          data-testid={`${ctaTestPrefix}-hero-body`}
        >
          {variant.body}
        </p>
        <div className="mt-7 flex flex-wrap gap-3">
          <Link
            to="/auth?tab=register"
            onClick={() => trackEvent("feature_cta_click", { page: ctaTestPrefix, cta: "trial", variant: variant.variantKey })}
            data-testid={`${ctaTestPrefix}-hero-cta-primary`}
          >
            <Button className="bg-[#116B6E] hover:bg-[#0F5658] text-white rounded-md h-11 px-5 text-sm font-semibold">
              Start your 30-day free trial
              <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </Link>
          <Link
            to="/pricing"
            onClick={() => trackEvent("feature_cta_click", { page: ctaTestPrefix, cta: "pricing", variant: variant.variantKey })}
            data-testid={`${ctaTestPrefix}-hero-cta-secondary`}
          >
            <Button
              variant="outline"
              className="border-[#D6F0EF] text-[#116B6E] dark:text-[#3FB8BC] hover:bg-[#F4FAFA] rounded-md h-11 px-5 text-sm font-semibold"
            >
              See pricing
            </Button>
          </Link>
        </div>
        <p className="text-xs text-[#6F828A] mt-4">
          No card on signup · cancel any time · €10.99/mo or €99/yr after trial.
        </p>
      </div>
      <div className="relative">
        <div className="absolute -inset-6 rounded-3xl bg-[#1F9FA4]/8 blur-3xl dark:bg-[#1F9FA4]/12" aria-hidden />
        <div className="relative">{mockup}</div>
      </div>
    </div>
  </section>
  );
};

// "What this gives you" — 3-up benefit grid with icons.
const BenefitsBlock = ({ benefits, testPrefix }) => (
  <section className="max-w-6xl mx-auto px-5 sm:px-8 py-14 lg:py-20">
    <div className="grid md:grid-cols-3 gap-4 lg:gap-6">
      {benefits.map((b) => (
        <div key={b.title} className={`${cardSurface} p-6`} data-testid={`${testPrefix}-benefit-${b.testid}`}>
          <div className="w-11 h-11 rounded-lg bg-[#E0F2F2] dark:bg-[#1F9FA4]/15 inline-flex items-center justify-center">
            <b.icon className="w-5 h-5 text-[#116B6E] dark:text-[#3FB8BC]" strokeWidth={1.6} />
          </div>
          <h3 className="font-heading text-lg font-semibold mt-5 text-[#1A2225] dark:text-[#F4F8F9]">
            {b.title}
          </h3>
          <p className="text-sm leading-relaxed mt-2 text-[#48595E] dark:text-[#A8B6BB]">
            {b.desc}
          </p>
        </div>
      ))}
    </div>
  </section>
);

// "Why parents use this" — supporting paragraph + bulleted reassurances.
const TrustBlock = ({ heading, lead, points, testPrefix }) => (
  <section className="bg-[#FAFCFC] dark:bg-[#0B1012] border-y border-[#E5EBEC] dark:border-[#1F2A2D]">
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14 lg:py-20 grid lg:grid-cols-5 gap-10">
      <div className="lg:col-span-2">
        <Eyebrow>Trusted &amp; auditable</Eyebrow>
        <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-3 text-[#1A2225] dark:text-[#F4F8F9]">
          {heading}
        </h2>
        <p className="text-base leading-relaxed mt-4 text-[#48595E] dark:text-[#A8B6BB]">
          {lead}
        </p>
      </div>
      <ul className="lg:col-span-3 grid sm:grid-cols-2 gap-3">
        {points.map((p) => (
          <li
            key={p}
            className={`${cardSurface} p-4 flex items-start gap-3`}
            data-testid={`${testPrefix}-trust-point`}
          >
            <CheckCircle2 className="w-5 h-5 text-[#1F9FA4] flex-shrink-0 mt-0.5" strokeWidth={1.8} />
            <span className="text-sm text-[#1A2225] dark:text-[#F4F8F9] leading-relaxed">{p}</span>
          </li>
        ))}
      </ul>
    </div>
  </section>
);

const FinalCTA = ({ headline, sub, testPrefix }) => (
  <section className="max-w-6xl mx-auto px-5 sm:px-8 py-16 lg:py-24 text-center">
    <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-[#1A2225] dark:text-[#F4F8F9]">
      {headline}
    </h2>
    <p className="text-base leading-relaxed mt-4 text-[#48595E] dark:text-[#A8B6BB] max-w-xl mx-auto">
      {sub}
    </p>
    <div className="mt-7 flex flex-wrap gap-3 justify-center">
      <Link
        to="/auth?tab=register"
        onClick={() => trackEvent("feature_cta_click", { page: testPrefix, cta: "trial_final" })}
        data-testid={`${testPrefix}-final-cta-primary`}
      >
        <Button className="bg-[#116B6E] hover:bg-[#0F5658] text-white rounded-md h-11 px-5 text-sm font-semibold">
          Start your 30-day free trial
          <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
      </Link>
      <Link
        to="/pricing"
        onClick={() => trackEvent("feature_cta_click", { page: testPrefix, cta: "pricing_final" })}
        data-testid={`${testPrefix}-final-cta-secondary`}
      >
        <Button
          variant="outline"
          className="border-[#D6F0EF] text-[#116B6E] dark:text-[#3FB8BC] hover:bg-[#F4FAFA] rounded-md h-11 px-5 text-sm font-semibold"
        >
          See pricing
        </Button>
      </Link>
    </div>
  </section>
);

// ---------------------------------------------------------------------------
// Mockups (faithful reproductions of the real app UI using the same React
// tokens — sharper than a screenshot, no browser chrome to clip).
// ---------------------------------------------------------------------------

const MockupFrame = ({ children, label }) => (
  <div
    className="rounded-2xl border border-[#E5EBEC] dark:border-[#1F2A2D] bg-white dark:bg-[#0E1416] shadow-[0_24px_48px_-24px_rgba(15,21,23,0.18)] overflow-hidden"
    data-testid="feature-mockup-frame"
    role="img"
    aria-label={label || "Product preview"}
  >
    <div className="h-7 bg-[#F4FAFA] dark:bg-[#101A1C] border-b border-[#E5EBEC] dark:border-[#1F2A2D] flex items-center px-3 gap-1.5">
      <span className="w-2 h-2 rounded-full bg-[#E6A3A3]" />
      <span className="w-2 h-2 rounded-full bg-[#E6C68C]" />
      <span className="w-2 h-2 rounded-full bg-[#A7D8DA]" />
      <span className="ml-3 text-[10px] uppercase tracking-[0.16em] font-semibold text-[#116B6E] dark:text-[#3FB8BC]">
        CoParent Console
      </span>
    </div>
    {children}
  </div>
);

// ----- Calendar mockup -----------------------------------------------------
const CalendarMockup = () => {
  const events = { 4: "#1F9FA4", 9: "#88A47C", 12: "#D7714C", 18: "#1F9FA4", 23: "#88A47C", 26: "#D7714C", 29: "#1F9FA4" };
  const cells = [];
  for (let i = 0; i < 3; i++) cells.push(null);
  for (let d = 1; d <= 30; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  return (
    <MockupFrame label="Calendar preview">
      <div className="p-5">
        <Eyebrow>Shared calendar</Eyebrow>
        <div className="font-heading text-xl font-semibold mt-1 text-[#1A2225] dark:text-[#F4F8F9]">
          May 2026
        </div>
        <div className="mt-4 grid grid-cols-7 gap-1 text-[10px] text-center uppercase tracking-[0.1em] text-[#6F828A] mb-1">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
            <div key={d}>{d.charAt(0)}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((d, i) => (
            <div
              key={d == null ? `blank-${i}` : `day-${d}`}
              className="aspect-square rounded-sm flex flex-col items-center justify-center bg-white dark:bg-[#0E1416] border border-[#EEF3F4] dark:border-[#1F2A2D] relative"
            >
              {d && (
                <>
                  <span className={`text-[11px] font-medium ${d === 12 ? "text-white" : "text-[#1A2225] dark:text-[#F4F8F9]"}`}>{d}</span>
                  {d === 12 && <span aria-hidden="true" className="absolute inset-0 -z-10 rounded-sm bg-[#116B6E]" />}
                  {events[d] && d !== 12 && (
                    <span className="w-1.5 h-1.5 rounded-full mt-0.5" style={{ backgroundColor: events[d] }} />
                  )}
                </>
              )}
            </div>
          ))}
        </div>
        <div className="mt-4 space-y-2">
          <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-[#6F828A]">Today · 12 May</div>
          <div className="rounded-md border border-[#E5EBEC] dark:border-[#1F2A2D] bg-white dark:bg-[#0E1416] px-3 py-2 flex items-center gap-2.5">
            <span className="w-1 h-8 rounded-full bg-[#1F9FA4]" />
            <div className="flex-1 min-w-0">
              <div className="text-[12px] font-medium text-[#1A2225] dark:text-[#F4F8F9]">Handover · Mia</div>
              <div className="text-[10px] text-[#6F828A]">Friday 20:00 · to Alex</div>
            </div>
          </div>
          <div className="rounded-md border border-[#E5EBEC] dark:border-[#1F2A2D] bg-white dark:bg-[#0E1416] px-3 py-2 flex items-center gap-2.5">
            <span className="w-1 h-8 rounded-full bg-[#88A47C]" />
            <div className="flex-1 min-w-0">
              <div className="text-[12px] font-medium text-[#1A2225] dark:text-[#F4F8F9]">School pickup · Noah</div>
              <div className="text-[10px] text-[#6F828A]">15:30 · Sam Parent</div>
            </div>
          </div>
        </div>
      </div>
    </MockupFrame>
  );
};

// ----- Expenses mockup -----------------------------------------------------
const ExpensesMockup = () => (
  <MockupFrame label="Expenses preview">
    <div className="p-5">
      <Eyebrow>Shared · EUR</Eyebrow>
      <div className="font-heading text-xl font-semibold mt-1 text-[#1A2225] dark:text-[#F4F8F9]">
        Expenses · May 2026
      </div>
      <div className="grid grid-cols-3 gap-2 mt-4">
        <div className="rounded-md border border-[#E5EBEC] dark:border-[#1F2A2D] bg-white dark:bg-[#0E1416] px-3 py-2.5">
          <div className="text-[9px] uppercase tracking-[0.14em] text-[#6F828A]">Logged</div>
          <div className="font-heading text-base font-semibold mt-0.5 text-[#1A2225] dark:text-[#F4F8F9]">€210</div>
        </div>
        <div className="rounded-md border border-[#E5EBEC] dark:border-[#1F2A2D] bg-white dark:bg-[#0E1416] px-3 py-2.5">
          <div className="text-[9px] uppercase tracking-[0.14em] text-[#6F828A]">Settled</div>
          <div className="font-heading text-base font-semibold mt-0.5 text-[#1F9FA4]">€6</div>
        </div>
        <div className="rounded-md border border-[#E5EBEC] dark:border-[#1F2A2D] bg-white dark:bg-[#0E1416] px-3 py-2.5">
          <div className="text-[9px] uppercase tracking-[0.14em] text-[#6F828A]">Outstanding</div>
          <div className="font-heading text-base font-semibold mt-0.5 text-[#C0291D]">€204</div>
        </div>
      </div>
      <div className="rounded-md bg-[#F4FAFA] dark:bg-[#0F2F30]/30 border border-[#D6F0EF] dark:border-[#1F9FA4]/30 px-3 py-2.5 mt-3">
        <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-[#116B6E] dark:text-[#3FB8BC]">Net settlement</div>
        <div className="flex items-baseline justify-between mt-1">
          <div className="text-[12px] text-[#1A2225] dark:text-[#F4F8F9]"><span className="font-semibold">Sam</span> owes <span className="font-semibold">you</span></div>
          <div className="font-heading font-semibold text-[#C0291D] text-base">€204.00</div>
        </div>
      </div>
      <div className="space-y-2 mt-3">
        {[
          { label: "Medicine", date: "12 May", amt: "€23.00", state: "Outstanding", colour: "#C0291D", split: "50/50" },
          { label: "School supplies", date: "12 May", amt: "€26.40", state: "Outstanding", colour: "#C0291D", split: "50/50" },
          { label: "Tutor", date: "07 May", amt: "€80.00", state: "Settled", colour: "#1F9FA4", split: "60/40" },
          { label: "Optician", date: "02 May", amt: "€60.00", state: "Settled", colour: "#1F9FA4", split: "50/50" },
        ].map((e) => (
          <div key={e.label} className="rounded-md border border-[#E5EBEC] dark:border-[#1F2A2D] bg-white dark:bg-[#0E1416] px-3 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0">
              <CircleDot className="w-3.5 h-3.5 flex-shrink-0" style={{ color: e.colour }} />
              <div className="min-w-0">
                <div className="text-[11px] font-medium text-[#1A2225] dark:text-[#F4F8F9] truncate">{e.label}</div>
                <div className="text-[9px] text-[#6F828A]">{e.date} · split {e.split}</div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Paperclip className="w-3 h-3 text-[#6F828A]" />
              <span className="text-[11px] font-mono-data text-[#1A2225] dark:text-[#F4F8F9]">{e.amt}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  </MockupFrame>
);

// ----- Messages mockup -----------------------------------------------------
const MessagesMockup = () => (
  <MockupFrame label="Messages preview">
    <div className="p-5">
      <Eyebrow>Secure log</Eyebrow>
      <div className="font-heading text-xl font-semibold mt-1 text-[#1A2225] dark:text-[#F4F8F9]">
        Messages · Mia
      </div>
      <div className="mt-4 space-y-2.5">
        <div className="flex items-end gap-2">
          <span className="w-7 h-7 rounded-full bg-[#88A47C] text-white text-[10px] font-semibold flex items-center justify-center flex-shrink-0">SP</span>
          <div className="rounded-2xl rounded-bl-md bg-[#F0F4F5] dark:bg-[#1F2A2D] px-3.5 py-2 max-w-[85%]">
            <div className="text-[12px] text-[#1A2225] dark:text-[#F4F8F9] leading-snug">Can you collect Mia at 17:00 instead of 18:00 on Friday? Football trial.</div>
            <div className="text-[9px] text-[#6F828A] mt-1">Sam · 14:02 · delivered</div>
          </div>
        </div>
        <div className="flex items-end gap-2 justify-end">
          <div className="rounded-2xl rounded-br-md bg-[#116B6E] text-white px-3.5 py-2 max-w-[85%]">
            <div className="text-[12px] leading-snug">Yes, no problem. I'll be at the school gates at 17:00.</div>
            <div className="text-[9px] text-[#A7D8DA] mt-1">Alex · 14:08 · read</div>
          </div>
          <span className="w-7 h-7 rounded-full bg-[#1F9FA4] text-white text-[10px] font-semibold flex items-center justify-center flex-shrink-0">AL</span>
        </div>
        <div className="flex items-end gap-2">
          <span className="w-7 h-7 rounded-full bg-[#88A47C] text-white text-[10px] font-semibold flex items-center justify-center flex-shrink-0">SP</span>
          <div className="rounded-2xl rounded-bl-md bg-[#F0F4F5] dark:bg-[#1F2A2D] px-3.5 py-2 max-w-[85%]">
            <div className="text-[12px] text-[#1A2225] dark:text-[#F4F8F9] leading-snug">Perfect. Mia's kit is in her room — thanks 🙏</div>
            <div className="text-[9px] text-[#6F828A] mt-1">Sam · 14:09</div>
          </div>
        </div>
      </div>
      <div className="mt-4 rounded-lg border border-[#E5EBEC] dark:border-[#1F2A2D] bg-[#FAFCFC] dark:bg-[#101A1C] p-3 flex items-start gap-2.5">
        <Lock className="w-4 h-4 text-[#116B6E] dark:text-[#3FB8BC] flex-shrink-0 mt-0.5" />
        <div className="text-[11px] text-[#48595E] dark:text-[#A8B6BB] leading-snug">
          Tamper-evident · every message is hashed at send and shows in your audit log. Nothing can be deleted or back-dated.
        </div>
      </div>
    </div>
  </MockupFrame>
);

// ----- Court Bundle mockup -------------------------------------------------
const CourtBundleMockup = () => (
  <MockupFrame label="Court bundle preview">
    <div className="p-5">
      <Eyebrow>Records</Eyebrow>
      <div className="font-heading text-xl font-semibold mt-1 text-[#1A2225] dark:text-[#F4F8F9]">
        Court bundle export
      </div>
      <div className="text-[12px] text-[#48595E] dark:text-[#A8B6BB] leading-relaxed mt-2">
        One signed PDF per child. Includes messages, custody history, expenses, journal entries and a tamper-evident fingerprint.
      </div>
      <div className="grid sm:grid-cols-3 gap-2 mt-4">
        {[
          { d: "Mia", colour: "#1F9FA4", pages: 142 },
          { d: "Noah", colour: "#88A47C", pages: 118 },
          { d: "E", colour: "#D7714C", pages: 73 },
        ].map((c) => (
          <div key={c.d} className="rounded-lg border border-[#E5EBEC] dark:border-[#1F2A2D] bg-[#FAFCFC] dark:bg-[#101A1C] p-3">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full" style={{ background: c.colour }} />
              <span className="text-[11px] font-medium text-[#1A2225] dark:text-[#F4F8F9]">{c.d}</span>
            </div>
            <div className="mt-3 rounded border border-[#E5EBEC] dark:border-[#1F2A2D] bg-white dark:bg-[#0E1416] p-2.5">
              <div className="space-y-1">
                <div className="h-1 bg-[#E5EBEC] dark:bg-[#1F2A2D] rounded w-3/4" />
                <div className="h-1 bg-[#E5EBEC] dark:bg-[#1F2A2D] rounded" />
                <div className="h-1 bg-[#E5EBEC] dark:bg-[#1F2A2D] rounded w-1/2" />
                <div className="h-1 bg-[#E5EBEC] dark:bg-[#1F2A2D] rounded w-5/6" />
                <div className="h-1 bg-[#E5EBEC] dark:bg-[#1F2A2D] rounded w-2/3" />
              </div>
              <div className="mt-2 pt-2 border-t border-[#E5EBEC] dark:border-[#1F2A2D] flex items-center justify-between text-[9px] text-[#6F828A]">
                <span className="font-mono-data">{c.pages} pp</span>
                <QrCode className="w-3 h-3" />
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 rounded-lg border border-[#D6F0EF] dark:border-[#1F9FA4]/30 bg-[#F4FAFA] dark:bg-[#0F2F30]/30 p-3 flex items-start gap-2.5">
        <Fingerprint className="w-4 h-4 text-[#116B6E] dark:text-[#3FB8BC] flex-shrink-0 mt-0.5" />
        <div className="text-[11px] text-[#1A2225] dark:text-[#F4F8F9] leading-snug">
          <span className="font-mono-data text-[10px]">SHA-256 · a4f7…3b1c</span> — solicitors and the court can verify each bundle hasn't been edited since export.
        </div>
      </div>
    </div>
  </MockupFrame>
);

// ----- Journal mockup ------------------------------------------------------
const JournalMockup = () => (
  <MockupFrame label="Journal preview">
    <div className="p-5">
      <Eyebrow>Incident log</Eyebrow>
      <div className="font-heading text-xl font-semibold mt-1 text-[#1A2225] dark:text-[#F4F8F9]">
        Journal
      </div>
      <div className="flex gap-2 mt-4">
        <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-white bg-[#116B6E] rounded-full px-2.5 py-1">All</span>
        <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#48595E] dark:text-[#A8B6BB] bg-[#F0F4F5] dark:bg-[#1F2A2D] rounded-full px-2.5 py-1">Shared</span>
        <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#48595E] dark:text-[#A8B6BB] bg-[#F0F4F5] dark:bg-[#1F2A2D] rounded-full px-2.5 py-1">Private</span>
      </div>
      <div className="mt-4 space-y-2.5">
        {[
          {
            child: "Mia", colour: "#1F9FA4", title: "Late pickup at school",
            body: "School rang at 15:45 — co-parent 25 mins late, no message in advance. Walked Mia home in the rain.",
            tags: ["Private", "Photo attached"], when: "12 May · 16:02",
          },
          {
            child: "Noah", colour: "#88A47C", title: "Doctor's visit — asthma check-up",
            body: "Dr. Walsh confirmed Noah's inhaler dose can stay the same. New prescription valid until Aug.",
            tags: ["Shared", "Receipt attached"], when: "08 May · 11:30",
          },
          {
            child: "E", colour: "#D7714C", title: "Parent-teacher meeting notes",
            body: "Maths struggling — Mrs. Ryan suggested 30 min reading time before bed. Will share homework plan with Sam.",
            tags: ["Shared"], when: "04 May · 18:15",
          },
        ].map((e) => (
          <div key={e.title} className="rounded-md border border-[#E5EBEC] dark:border-[#1F2A2D] bg-white dark:bg-[#0E1416] px-3 py-2.5">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full" style={{ background: e.colour }} />
              <span className="text-[10px] uppercase tracking-[0.12em] text-[#6F828A] font-semibold">{e.child}</span>
              <span className="text-[10px] text-[#6F828A] ml-auto font-mono-data">{e.when}</span>
            </div>
            <div className="text-[12px] font-medium text-[#1A2225] dark:text-[#F4F8F9] mt-1.5">{e.title}</div>
            <div className="text-[11px] text-[#48595E] dark:text-[#A8B6BB] mt-1 leading-snug">{e.body}</div>
            <div className="flex gap-1.5 mt-2">
              {e.tags.map((t) => (
                <span key={t} className="text-[9px] uppercase tracking-[0.1em] font-semibold text-[#116B6E] dark:text-[#3FB8BC] bg-[#E0F2F2] dark:bg-[#1F9FA4]/15 rounded px-1.5 py-0.5">{t}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  </MockupFrame>
);

// ----- Overview / index mockup --------------------------------------------
const OverviewMockup = () => (
  <MockupFrame label="App preview">
    <div className="p-5">
      <Eyebrow>Five tools, one calm app</Eyebrow>
      <div className="font-heading text-xl font-semibold mt-1 text-[#1A2225] dark:text-[#F4F8F9]">
        Built for separated parents
      </div>
      <div className="grid grid-cols-2 gap-2 mt-4">
        {[
          { Icon: Calendar, label: "Calendar", sub: "Handovers · approvals" },
          { Icon: ReceiptEuro, label: "Expenses", sub: "Split · receipts" },
          { Icon: MessageSquare, label: "Messages", sub: "Tamper-evident" },
          { Icon: BookOpen, label: "Journal", sub: "Private or shared" },
          { Icon: ScrollText, label: "Court bundle", sub: "One signed PDF" },
          { Icon: ShieldCheck, label: "Audit log", sub: "Every action · forever" },
        ].map((x) => (
          <div key={x.label} className="rounded-lg border border-[#E5EBEC] dark:border-[#1F2A2D] bg-[#FAFCFC] dark:bg-[#101A1C] p-3">
            <x.Icon className="w-4 h-4 text-[#116B6E] dark:text-[#3FB8BC]" strokeWidth={1.7} />
            <div className="text-[11px] font-semibold mt-2 text-[#1A2225] dark:text-[#F4F8F9]">{x.label}</div>
            <div className="text-[9px] text-[#6F828A] mt-0.5">{x.sub}</div>
          </div>
        ))}
      </div>
    </div>
  </MockupFrame>
);

// ---------------------------------------------------------------------------
// The six pages
// ---------------------------------------------------------------------------

const PageShell = ({ children }) => (
  <div className="min-h-screen bg-white dark:bg-[#0E1416] text-[#1A2225] dark:text-[#F4F8F9]">
    <MarketingHeader />
    <main>{children}</main>
    <MarketingFooter />
  </div>
);

// ----- /features (overview hub) --------------------------------------------
export function FeaturesOverview() {
  useDocumentMeta({
    title: "Features — CoParent Console: Calendar, Expenses, Messaging, Court Bundle, Journal",
    description: "Everything separated parents need in one calm, accountable app: shared calendar, expenses with receipts, tamper-evident messaging, court-ready PDF exports, and a private incident journal.",
  });
  const features = [
    { slug: "calendar", Icon: Calendar, title: "Shared calendar", desc: "Handovers, school pickups, holidays — propose, approve, done." },
    { slug: "expenses", Icon: ReceiptEuro, title: "Expenses", desc: "Track who paid what, split fairly, attach receipts, settle clearly." },
    { slug: "messaging", Icon: MessageSquare, title: "Messaging", desc: "Tamper-evident chat that holds up in court. Nothing edited, nothing lost." },
    { slug: "court-bundle", Icon: ScrollText, title: "Court bundle", desc: "One signed PDF with everything a solicitor or judge actually wants." },
    { slug: "journal", Icon: BookOpen, title: "Journal", desc: "Private incident log — share with your co-parent when you choose." },
  ];
  return (
    <PageShell>
      <Hero
        pageKey="overview"
        eyebrow="Features"
        headline="Co-parenting that finally feels organised."
        body="Five focused tools, one calm app. Everything you log is timestamped, child-scoped, and ready to export — so the next disagreement doesn't become a court case."
        ctaTestPrefix="features-overview"
        mockup={<OverviewMockup />}
      />
      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-14 lg:py-20">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
          {features.map((f) => (
            <Link
              key={f.slug}
              to={`/features/${f.slug}`}
              className={`${cardSurface} p-6 group transition-shadow hover:shadow-[0_8px_24px_-12px_rgba(31,159,164,0.18)]`}
              data-testid={`features-overview-link-${f.slug}`}
            >
              <div className="w-11 h-11 rounded-lg bg-[#E0F2F2] dark:bg-[#1F9FA4]/15 inline-flex items-center justify-center">
                <f.Icon className="w-5 h-5 text-[#116B6E] dark:text-[#3FB8BC]" strokeWidth={1.6} />
              </div>
              <h3 className="font-heading text-lg font-semibold mt-5 text-[#1A2225] dark:text-[#F4F8F9] flex items-center gap-2">
                {f.title}
                <ArrowRight className="w-4 h-4 text-[#116B6E] dark:text-[#3FB8BC] opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition" />
              </h3>
              <p className="text-sm leading-relaxed mt-2 text-[#48595E] dark:text-[#A8B6BB]">{f.desc}</p>
            </Link>
          ))}
        </div>
      </section>
      <TrustBlock
        heading="Calm by design. Auditable by default."
        lead="Every action in CoParent Console is timestamped and child-scoped. Nothing can be deleted or back-dated. The data your co-parent sees is exactly the data the court would see."
        points={[
          "Per-child privacy — co-parents only ever see their own children.",
          "Tamper-evident — SHA-256 fingerprints on every export.",
          "Irish-made — built for European GDPR from day one.",
          "30-day free trial · no card on signup · cancel any time.",
        ]}
        testPrefix="features-overview"
      />
      <FinalCTA
        headline="See how it feels to be organised."
        sub="Start your 30-day free trial — no card on signup."
        testPrefix="features-overview"
      />
    </PageShell>
  );
}

// ----- /features/calendar --------------------------------------------------
export function FeatureCalendar() {
  useDocumentMeta({
    title: "Shared calendar for separated parents — CoParent Console",
    description: "Plan handovers, school pickups, holidays and routines together. Every change is proposed, approved and timestamped — so there's no 'I never agreed to that'.",
  });
  return (
    <PageShell>
      <Hero
        pageKey="calendar"
        eyebrow="Shared calendar"
        headline="Never argue about pickup time again."
        body="Plan handovers, school runs, holidays and overnights in one shared calendar your co-parent actually sees. Every change is proposed, approved and timestamped — so when one of you says 'we agreed Friday', the calendar proves it."
        ctaTestPrefix="features-calendar"
        mockup={<CalendarMockup />}
      />
      <BenefitsBlock
        testPrefix="features-calendar"
        benefits={[
          { icon: Repeat, title: "Recurring routines", desc: "Set up your weekly schedule once. Override single dates without breaking the pattern.", testid: "routines" },
          { icon: CheckCircle2, title: "Propose → approve workflow", desc: "Either parent suggests a change. The other approves or counter-proposes. Nothing changes silently.", testid: "approvals" },
          { icon: Clock, title: "Handover countdown", desc: "Both phones show the next handover with full context — who, where, what kit travels with the child.", testid: "handovers" },
        ]}
      />
      <TrustBlock
        heading="The calendar is the contract."
        lead="A shared visual schedule replaces 200 WhatsApp messages with a single source of truth. Every event has a history — you can see who proposed it, who approved it, and when. Disputes don't escalate when the answer is on screen."
        points={[
          "Per-child colour coding so blended families stay readable.",
          "Holiday and school-term overlays you can toggle.",
          "Export any month or year as a single PDF for solicitors.",
          "Push reminders on handover days — opt-in, per child.",
        ]}
        testPrefix="features-calendar"
      />
      <FinalCTA
        headline="Bring your schedule into one calm view."
        sub="Free 30-day trial. Add your co-parent in under 60 seconds."
        testPrefix="features-calendar"
      />
    </PageShell>
  );
}

// ----- /features/expenses --------------------------------------------------
export function FeatureExpenses() {
  useDocumentMeta({
    title: "Track shared expenses for separated parents — CoParent Console",
    description: "Log every euro you spend on the children, attach the receipt, and let the app calculate what you're owed. No spreadsheets, no chasing payments, no awkward conversations.",
  });
  return (
    <PageShell>
      <Hero
        pageKey="expenses"
        eyebrow="Expenses"
        headline="Stop arguing about €23."
        body="Log every euro you spend on the children, attach the receipt, and let CoParent Console calculate what your co-parent owes you. Mark payments as received, dispute one if you need to, and walk into mediation with a clean statement."
        ctaTestPrefix="features-expenses"
        mockup={<ExpensesMockup />}
      />
      <BenefitsBlock
        testPrefix="features-expenses"
        benefits={[
          { icon: Paperclip, title: "Receipt vault", desc: "Snap a photo at the till. Every receipt is stored encrypted, tagged to the expense, and ready to export.", testid: "receipts" },
          { icon: ReceiptEuro, title: "Fair splits", desc: "50/50, 60/40, 70/30, or custom by parent. Set a household default once — override per expense when you need to.", testid: "splits" },
          { icon: CheckCircle2, title: "Settle clearly", desc: "Record a payment, confirm receipt, or dispute it. Every settlement leaves a trail.", testid: "settle" },
        ]}
      />
      <TrustBlock
        heading="Spreadsheets weren't designed for this."
        lead="Most separated parents track expenses in a WhatsApp thread or a shared spreadsheet. Both lose receipts. Both create arguments. CoParent Console keeps every entry timestamped, scoped to the child it relates to, and proof against the 'I already paid you back' problem."
        points={[
          "Per-child filtering — see exactly what each child cost this month.",
          "Monthly PDF and snapshot exports for your solicitor or accountant.",
          "Outstanding-owed totals at a glance, in red until they're settled.",
          "Dispute a payment with a reason — it goes straight to the audit log.",
        ]}
        testPrefix="features-expenses"
      />
      <FinalCTA
        headline="Make every receipt count."
        sub="Free 30-day trial · no card on signup."
        testPrefix="features-expenses"
      />
    </PageShell>
  );
}

// ----- /features/messaging -------------------------------------------------
export function FeatureMessaging() {
  useDocumentMeta({
    title: "Court-admissible messaging for separated parents — CoParent Console",
    description: "A tamper-evident chat designed for high-conflict co-parenting. Nothing edited. Nothing deleted. Every message is timestamped and exportable as a court-ready record.",
  });
  return (
    <PageShell>
      <Hero
        pageKey="messaging"
        eyebrow="Messaging"
        headline="Every message. On the record."
        body="A calm, structured chat designed for high-conflict co-parenting. Messages are tamper-evident, timestamped at send, child-scoped, and exportable as a single signed PDF. Nothing can be edited. Nothing can be deleted. Nothing can be back-dated."
        ctaTestPrefix="features-messaging"
        mockup={<MessagesMockup />}
      />
      <BenefitsBlock
        testPrefix="features-messaging"
        benefits={[
          { icon: Lock, title: "Tamper-evident", desc: "Every message is hashed at send. Any tampering breaks the chain — visible to you and your solicitor.", testid: "tamper" },
          { icon: Users, title: "Per-child threads", desc: "Conversations stay scoped to the child they're about. Blended families don't bleed.", testid: "scoped" },
          { icon: ScrollText, title: "Court-ready export", desc: "Generate a PDF of any date range. Solicitors get the full thread with hash verification on the cover page.", testid: "export" },
        ]}
      />
      <TrustBlock
        heading="WhatsApp wasn't designed for separation."
        lead="It lets messages be deleted, edited, and screenshotted out of context. CoParent Console is built around the opposite assumption: when a co-parenting conversation matters, it should survive in full, in order, and in plain English."
        points={[
          "Read receipts and delivery state — no more 'I never got that'.",
          "Sentiment-aware tone hints (optional) flag escalating language before you hit send.",
          "Mediator and solicitor read-only access — strictly per-child, time-limited.",
          "Search across every message you've ever sent. By keyword, by child, by date.",
        ]}
        testPrefix="features-messaging"
      />
      <FinalCTA
        headline="Take the heat out of every reply."
        sub="Free 30-day trial · cancel any time."
        testPrefix="features-messaging"
      />
    </PageShell>
  );
}

// ----- /features/court-bundle ----------------------------------------------
export function FeatureCourtBundle() {
  useDocumentMeta({
    title: "Court bundle PDF for solicitors and family courts — CoParent Console",
    description: "Generate the exact document your solicitor wants in one click: messages, custody history, expenses, journal entries and a tamper-evident fingerprint. One signed PDF per child.",
  });
  return (
    <PageShell>
      <Hero
        pageKey="court-bundle"
        eyebrow="Court bundle"
        headline="What solicitors actually want, in one click."
        body="Generate a signed PDF that consolidates messages, custody history, expenses, journal entries and the full audit log for any date range. One bundle per child, with a tamper-evident SHA-256 fingerprint and a QR code your solicitor can verify in seconds."
        ctaTestPrefix="features-court-bundle"
        mockup={<CourtBundleMockup />}
      />
      <BenefitsBlock
        testPrefix="features-court-bundle"
        benefits={[
          { icon: ScrollText, title: "Solicitor-ready format", desc: "Cover page, table of contents, paginated sections, child-scoped throughout. Print-ready.", testid: "format" },
          { icon: Fingerprint, title: "Tamper-evident fingerprint", desc: "Every bundle is hashed. Any edit invalidates the hash, so the court knows it's the original.", testid: "fingerprint" },
          { icon: QrCode, title: "QR verification", desc: "Solicitors and judges scan the cover-page QR to confirm authenticity against your account.", testid: "qr" },
        ]}
      />
      <TrustBlock
        heading="Built with Irish family law in mind."
        lead="We worked with separated parents, mediators and family-law solicitors to design a bundle that lands well in real proceedings. It's the document your solicitor would have asked you to compile anyway — but generated in under a minute, with no missing receipts and no 'I'll send the rest tomorrow'."
        points={[
          "Choose any date range — last 30 days, last year, custom.",
          "Per-child or combined bundle — bundles split for blended cases.",
          "Includes the full audit log: every login, edit, export, every action.",
          "Mediator / solicitor accounts can request read-only access without ever owning the data.",
        ]}
        testPrefix="features-court-bundle"
      />
      <FinalCTA
        headline="Walk into your next meeting prepared."
        sub="Start logging today, export when you need to. Free 30-day trial."
        testPrefix="features-court-bundle"
      />
    </PageShell>
  );
}

// ----- /features/journal ---------------------------------------------------
export function FeatureJournal() {
  useDocumentMeta({
    title: "Private incident journal for separated parents — CoParent Console",
    description: "Log incidents, doctor's visits, school updates and routine notes — privately, or shared with your co-parent when you choose. Photos and receipts attach in seconds.",
  });
  return (
    <PageShell>
      <Hero
        pageKey="journal"
        eyebrow="Journal"
        headline="Remember every detail. Share only what matters."
        body="A calm place to log the small things — a late pickup, a doctor's note, a parent-teacher meeting. Keep entries private to yourself, or share with your co-parent when you choose. Photos and receipts attach in seconds. Every entry is timestamped and search-ready."
        ctaTestPrefix="features-journal"
        mockup={<JournalMockup />}
      />
      <BenefitsBlock
        testPrefix="features-journal"
        benefits={[
          { icon: Eye, title: "Private by default", desc: "Every journal entry starts private. You decide if and when to share it with your co-parent.", testid: "private" },
          { icon: Camera, title: "Photo & receipt attached", desc: "Capture the bruise, the prescription, the school letter. Stored encrypted, ready to export.", testid: "attach" },
          { icon: AlertCircle, title: "Incident-ready", desc: "When something concerning happens, log it the same day. Solicitors and mediators value contemporaneous notes.", testid: "incident" },
        ]}
      />
      <TrustBlock
        heading="Memory fades. The record doesn't."
        lead="Separated parents are asked to remember dates, conversations, and concerns months — sometimes years — after the fact. CoParent Console gives you a calm, private place to write things down the same day, so when memory fades, the timestamps don't."
        points={[
          "Per-child journal — entries auto-scope to the relevant child.",
          "Convert any journal entry to a shared note when you're ready.",
          "Included in court-bundle exports automatically.",
          "Full-text search across every entry you've ever written.",
        ]}
        testPrefix="features-journal"
      />
      <FinalCTA
        headline="Start your private record today."
        sub="Free 30-day trial · your entries stay yours."
        testPrefix="features-journal"
      />
    </PageShell>
  );
}

// Default export for convenience — the overview page.
export default FeaturesOverview;
